<script setup lang="ts">
import { computed, inject } from 'vue'
import AdvancedSettings from './AdvancedSettings.vue'
import BinaryReferencePicker from './BinaryReferencePicker.vue'
import { EMPTY_REFERENCE } from './binary-reference'
import { movedPart } from './concat-parts'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import type { ConcatServiceModel, FieldValue } from '@/types'
import type { ScopeDescriptor } from '@/types/bbel-types'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'

const props = defineProps<{
  modelValue: ConcatServiceModel
}>()

const emit = defineEmits<{
  'update:modelValue': [value: ConcatServiceModel]
}>()

const scopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => scopeCtx?.value ?? EMPTY_BBEL_SCOPE)

function updateParts(parts: FieldValue[]) {
  emit('update:modelValue', { ...props.modelValue, parts })
}

function setPart(index: number, part: FieldValue) {
  updateParts(props.modelValue.parts.map((existing, at) => (at === index ? part : existing)))
}

function addPart() {
  updateParts([...props.modelValue.parts, { $type: 'expression', expression: '' }])
}

function removePart(index: number) {
  updateParts(props.modelValue.parts.filter((_, at) => at !== index))
}

function movePart(index: number, offset: -1 | 1) {
  updateParts(movedPart(props.modelValue.parts, index, offset))
}
</script>

<template>
  <div>
    <div class="mb-3">
      <div class="form-label">Parts</div>

      <div v-for="(part, index) in modelValue.parts" :key="index" class="mb-2">
        <div class="d-flex flex-wrap align-items-center gap-3 mb-1">
          <span class="small text-body-secondary me-auto">Part {{ index + 1 }}</span>
          <button v-if="index > 0" type="button" class="btn btn-link btn-sm p-0" @click="movePart(index, -1)">
            Move up
          </button>
          <button
            v-if="index < modelValue.parts.length - 1"
            type="button"
            class="btn btn-link btn-sm p-0"
            @click="movePart(index, 1)"
          >
            Move down
          </button>
          <!-- A Concat always has one part, so the last one left cannot be removed. -->
          <button
            v-if="modelValue.parts.length > 1"
            type="button"
            class="btn btn-link btn-sm p-0 text-danger"
            @click="removePart(index)"
          >
            Remove
          </button>
        </div>

        <!-- A part that holds a source: the picker, with the way back to text in its menu. -->
        <BinaryReferencePicker
          v-if="part.$type === 'binary'"
          :label="`Part ${index + 1}`"
          :model-value="part.reference"
          @update:model-value="setPart(index, { $type: 'binary', reference: $event })"
        >
          <template #footer="{ close }">
            <button
              type="button"
              class="btn btn-link btn-sm p-0"
              @click="setPart(index, { $type: 'expression', expression: '' }); close()"
            >
              Write text instead
            </button>
          </template>
        </BinaryReferencePicker>

        <!-- A part that holds text: the expression field, and a chevron that opens the same source tree.
             A pick turns the part into a source. -->
        <BinaryReferencePicker
          v-else
          :model-value="EMPTY_REFERENCE"
          @update:model-value="setPart(index, { $type: 'binary', reference: $event })"
        >
          <template #field="{ open, toggle }">
            <div class="d-flex align-items-start gap-2">
              <div class="flex-grow-1 concat-text">
                <BbelField
                  :model-value="part.expression"
                  :scope="scope"
                  field-shape="body"
                  placeholder="Type text, or pick a source"
                  @update:model-value="setPart(index, { $type: 'expression', expression: $event })"
                />
              </div>
              <button
                type="button"
                class="bb-btn-secondary-cta btn-sm"
                :aria-expanded="open"
                :aria-label="`Pick a source for Part ${index + 1}`"
                @click="toggle"
              >
                <i class="bi bi-chevron-down"></i>
              </button>
            </div>
          </template>
        </BinaryReferencePicker>
      </div>

      <button type="button" class="bb-btn-secondary-cta btn-sm" @click="addPart">
        <i class="bi bi-plus"></i> Add part
      </button>
      <div class="form-text">Nothing is added between parts, so a line break is typed into the part.</div>
    </div>

    <AdvancedSettings />
  </div>
</template>

<style scoped>
/* A flex child will not shrink below its content without this, and a long line would push the chevron out. */
.concat-text {
  min-width: 0;
}
</style>
